import { useState } from "react";
import { useIntl } from "react-intl";
import { useQueryClient, useQueries } from "@tanstack/react-query";
import { userQueryKeys } from "api/queries/user/userQueryKeys";

import usePermissions from "hooks/usePermissions";
import RefreshCache from "components/molecules/RefreshCache/RefreshCache";

import "./WelcomeWidget.scss";

type QueryKeyArrays = {
  [K in keyof typeof userQueryKeys]: typeof userQueryKeys[K] extends string[] ? K : never;
}[keyof typeof userQueryKeys];

const dashboardQueryKeys: QueryKeyArrays[] = [
  "orderStatus",
  "quoteRequestStatus",
  "shipmentStatus",
  "invoiceStatus",
  "bookedGraph",
  "recentUpdates",
  "taskWidget",
];

function WelcomeWidget() {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { permissions } = usePermissions();
  const { first_name } = JSON.parse(permissions || "{}");
  const [dataUpdatedAt, setDataUpdatedAt] = useState(new Date().toISOString());

  // Use useQueries to track the fetching state of all the queries
  const queries = useQueries({
    queries: dashboardQueryKeys.map((key) => ({
      queryKey: userQueryKeys[key],
      queryFn: () => {
        const data = queryClient.getQueryData(userQueryKeys[key]);
        if (data === undefined) return {};
        return data;
      },
      enabled: true,
    })),
  });

  const isFetching = queries.some((query) => query.isFetching);

  const handleClickRefresh = () => {
    dashboardQueryKeys.forEach((key) => {
      queryClient.invalidateQueries([userQueryKeys[key]]);
    });
    const latestDataUpdatedAt = new Date().toISOString();
    setDataUpdatedAt(latestDataUpdatedAt);
  };

  const getTimeOfDay = () => {
    const currentHour = new Date().getHours();

    if (currentHour >= 4 && currentHour < 12)
      return intl.formatMessage({ id: "general.morning", defaultMessage: "morning" });
    if (currentHour >= 12 && currentHour < 18)
      return intl.formatMessage({ id: "general.afternoon", defaultMessage: "afternoon" });
    return intl.formatMessage({ id: "general.evening", defaultMessage: "evening" });
  };

  const generateWelcomeMessage = () => {
    const timeOfDay = getTimeOfDay();
    const good = intl.formatMessage({ id: "general.good", defaultMessage: "Good" });
    return `${good} ${timeOfDay}, ${first_name}`;
  };

  return (
    <div className="welcome-widget-container">
      <div className="welcome-widget__title text-primary display-xs" style={{ fontWeight: 700 }}>
        {generateWelcomeMessage()}
      </div>
      <div className="welcome-widget__refresh">
        <RefreshCache dataUpdatedAt={dataUpdatedAt} onRefresh={handleClickRefresh} isLoading={isFetching} />
      </div>
    </div>
  );
}

export default WelcomeWidget;
